import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { VfxGradientAtlas } from "../textures/vfx_gradient_atlas.js";

import { AttributeSize } from "../types/core_types.js";

export const RadialStreakLayout = {
  aPosGrad:     AttributeSize.Vec4, // x, y, z, gradient_row
  aVelDecel:    AttributeSize.Vec4, // vx, vy, vz, deceleration_power
  aScaleParams: AttributeSize.Vec4, // width (length along flight), height (thickness across flight), start_scale, end_scale
  aTimeParams:  AttributeSize.Vec4, // start_time, duration, shrink_start_progress, dissolve_start_progress
  aNoiseParams: AttributeSize.Vec4, // noise_scale, dissolve_direction_weight, dissolve_softness, unused
  aUvBounds:    AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export class VfxRadialStreakBatch extends InstanceBatch<typeof RadialStreakLayout>
{
  private static readonly MAX_INSTANCES : number = 80;
  private static readonly RENDER_ORDER  : number = 29;

  private static cached_geometry : THREE.BufferGeometry | null = null;
  private uvs_initialized        : boolean = false;

  public constructor()
  {
    const base_geometry = VfxRadialStreakBatch.getGeometry();

    let atlas_texture: THREE.Texture | null = null;
    let gradient_texture: THREE.Texture | null = null;
    const noise_uv = new THREE.Vector4( 0, 0, 1, 1 );

    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
      const n_rect = Vfx3DGlobalAtlas.getUVRect( ResourceID.CloudNoiseTexture );
      noise_uv.set( n_rect.min_x, n_rect.min_y, n_rect.max_x, n_rect.max_y );
    }
    catch ( _e ) {}
    try { gradient_texture = VfxGradientAtlas.getTexture(); } catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:            { value: 0.0 },
        uTexture:         { value: atlas_texture },
        uGradientTexture: { value: gradient_texture },
        uNoiseUv:         { value: noise_uv },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosGrad;
        attribute vec4 aVelDecel;
        attribute vec4 aScaleParams; // x: length along velocity, y: thickness across velocity, z: start_scale, w: end_scale
        attribute vec4 aTimeParams;  // x: start_time, y: duration, z: shrink_start, w: dissolve_start
        attribute vec4 aNoiseParams; // x: noise_scale, y: direction_weight, z: softness, w: unused
        attribute vec4 aUvBounds;    // min_x, min_y, max_x, max_y

        varying vec2 vQuadUv;
        varying vec2 vSparkAtlasUv;
        varying vec2 vNoiseScaleWeight;
        varying vec3 vDissolveParams;
        varying float vFadeIn;
        varying float vGradientY;

        void main() {
          float start_time = aTimeParams.x;
          float duration = aTimeParams.y;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );
          
          vQuadUv = uv;
          vSparkAtlasUv = mix( aUvBounds.xy, aUvBounds.zw, uv );
          vGradientY = aPosGrad.w;
          vNoiseScaleWeight = aNoiseParams.xy;

          // Dissolve & fade parameters precomputed in vertex shader
          float dissolve_start = aTimeParams.w;
          float softness = aNoiseParams.z;
          float dissolve_active = progress > dissolve_start ? 1.0 : 0.0;
          float dissolve_t = clamp( ( progress - dissolve_start ) / max( 1.0 - dissolve_start, 0.001 ), 0.0, 1.0 );
          float threshold = mix( -softness, 1.0 + softness, dissolve_t );
          vDissolveParams = vec3( threshold, threshold + softness, dissolve_active );
          vFadeIn = clamp( progress / 0.05, 0.0, 1.0 );

          // Deceleration curve for realistic burst
          float deceleration_power = aVelDecel.w;
          vec3 velocity = aVelDecel.xyz;
          float ease_dist = 1.0 - pow( 1.0 - progress, deceleration_power );
          vec3 computed_pos = aPosGrad.xyz + velocity * duration * ease_dist;

          // Shrink calculation
          float shrink_start = aTimeParams.z;
          float scale_t = clamp( ( progress - shrink_start ) / max( 1.0 - shrink_start, 0.001 ), 0.0, 1.0 );
          float current_scale = mix( aScaleParams.z, aScaleParams.w, scale_t );

          // 2D flight direction in view space (modelViewMatrix)
          vec3 view_vel = ( modelViewMatrix * vec4( velocity, 0.0 ) ).xyz;
          vec2 dir = length( view_vel.xy ) > 0.001 ? normalize( view_vel.xy ) : vec2( 1.0, 0.0 );

          // Rotate quad so +X points along velocity vector
          mat2 rot = mat2(
            dir.x,  dir.y,
           -dir.y,  dir.x
          );

          vec2 local_scaled = position.xy * aScaleParams.xy * current_scale;
          vec2 rotated_pos = rot * local_scaled;

          vec3 billboard_local = vec3( rotated_pos, 0.0 );
          vec4 mv_position = modelViewMatrix * vec4( computed_pos, 1.0 );
          mv_position.xyz += billboard_local;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform sampler2D uGradientTexture;
        uniform vec4 uNoiseUv;

        varying vec2 vQuadUv;
        varying vec2 vSparkAtlasUv;
        varying vec2 vNoiseScaleWeight;
        varying vec3 vDissolveParams;
        varying float vFadeIn;
        varying float vGradientY;

        void main() {
          vec4 spark_sample = texture2D( uTexture, vSparkAtlasUv );
          
          float grad_x = clamp( 1.0 - vQuadUv.x, 0.0, 1.0 );
          vec4 gradient_val = texture2D( uGradientTexture, vec2( grad_x, vGradientY ) );
          vec3 spark_color = gradient_val.rgb;

          float noise_scale = vNoiseScaleWeight.x;
          float direction_weight = vNoiseScaleWeight.y;

          // Radial / Cloud Noise Dissolve
          float spatial_gradient = vQuadUv.x;
          vec2 noise_atlas_uv = mix( uNoiseUv.xy, uNoiseUv.zw, fract( vQuadUv * noise_scale ) );
          float noise_val = texture2D( uTexture, noise_atlas_uv ).r;
          float metric = mix( noise_val, spatial_gradient, direction_weight );

          float raw_dissolve = smoothstep( vDissolveParams.x, vDissolveParams.y, metric );
          float dissolve_alpha = mix( 1.0, raw_dissolve, vDissolveParams.z );

          float final_alpha = spark_sample.a * dissolve_alpha * vFadeIn * gradient_val.a;

          gl_FragColor = vec4( spark_color * final_alpha, final_alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxRadialStreakBatch.MAX_INSTANCES, layout: RadialStreakLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxRadialStreakBatch.RENDER_ORDER;
  }

  private static getGeometry(): THREE.BufferGeometry
  {
    if ( VfxRadialStreakBatch.cached_geometry )
      return VfxRadialStreakBatch.cached_geometry;

    const geom = new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxRadialStreakBatch.cached_geometry = geom;
    return geom;
  }

  public spawnParticle(
    gradient_row: number,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    width: number,
    height: number,
    start_scale: number,
    end_scale: number,
    shrink_start_progress: number,
    dissolve_start_progress: number,
    start_time: number,
    duration: number,
    res_id: ResourceID = ResourceID.SparkGlow4,
    noise_scale: number = 1.0,
    dissolve_direction_weight: number = 0.5,
    dissolve_softness: number = 0.25,
    deceleration_power: number = 2.2
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !this.uvs_initialized || !mat.uniforms.uTexture.value )
    {
      try
      {
        const noise_uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.CloudNoiseTexture );
        mat.uniforms.uNoiseUv.value.set( noise_uv.min_x, noise_uv.min_y, noise_uv.max_x, noise_uv.max_y );
        mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture();
        mat.uniforms.uGradientTexture.value = VfxGradientAtlas.getTexture();
        this.uvs_initialized = true;
      }
      catch ( _e ) {}
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const spark_uv = Vfx3DGlobalAtlas.getUVRect( res_id );

    const pos_grad_attr     = this.geometry.getAttribute( "aPosGrad" ).array as Float32Array;
    const vel_decel_attr    = this.geometry.getAttribute( "aVelDecel" ).array as Float32Array;
    const scale_params_attr = this.geometry.getAttribute( "aScaleParams" ).array as Float32Array;
    const time_params_attr  = this.geometry.getAttribute( "aTimeParams" ).array as Float32Array;
    const noise_params_attr = this.geometry.getAttribute( "aNoiseParams" ).array as Float32Array;
    const uv_bounds_attr    = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;

    pos_grad_attr[ idx * 4 ]     = start_pos.x;
    pos_grad_attr[ idx * 4 + 1 ] = start_pos.y;
    pos_grad_attr[ idx * 4 + 2 ] = start_pos.z;
    pos_grad_attr[ idx * 4 + 3 ] = gradient_row;

    vel_decel_attr[ idx * 4 ]     = velocity.x;
    vel_decel_attr[ idx * 4 + 1 ] = velocity.y;
    vel_decel_attr[ idx * 4 + 2 ] = velocity.z;
    vel_decel_attr[ idx * 4 + 3 ] = deceleration_power;

    scale_params_attr[ idx * 4 ]     = height;
    scale_params_attr[ idx * 4 + 1 ] = width;
    scale_params_attr[ idx * 4 + 2 ] = start_scale;
    scale_params_attr[ idx * 4 + 3 ] = end_scale;

    time_params_attr[ idx * 4 ]     = start_time;
    time_params_attr[ idx * 4 + 1 ] = duration;
    time_params_attr[ idx * 4 + 2 ] = shrink_start_progress;
    time_params_attr[ idx * 4 + 3 ] = dissolve_start_progress;

    noise_params_attr[ idx * 4 ]     = noise_scale;
    noise_params_attr[ idx * 4 + 1 ] = dissolve_direction_weight;
    noise_params_attr[ idx * 4 + 2 ] = dissolve_softness;
    noise_params_attr[ idx * 4 + 3 ] = 1.0;

    uv_bounds_attr[ idx * 4 ]     = spark_uv.min_x;
    uv_bounds_attr[ idx * 4 + 1 ] = spark_uv.min_y;
    uv_bounds_attr[ idx * 4 + 2 ] = spark_uv.max_x;
    uv_bounds_attr[ idx * 4 + 3 ] = spark_uv.max_y;

    this.geometry.getAttribute( "aPosGrad" ).needsUpdate     = true;
    this.geometry.getAttribute( "aVelDecel" ).needsUpdate    = true;
    this.geometry.getAttribute( "aScaleParams" ).needsUpdate = true;
    this.geometry.getAttribute( "aTimeParams" ).needsUpdate  = true;
    this.geometry.getAttribute( "aNoiseParams" ).needsUpdate = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate    = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
